import type { AffinityResolution } from './ghostAffinity';

export const MAX_LEVEL = 30;

export type DerivedStats = {
  maxHp: number;
  atk: number;
  defense: number;
  spirit: number;
  critRate: number;
  critDamage: number;
  haste: number;
  breakPower: number;
};

export type DamageResult = { damage: number; critical: boolean };

export function derivedStats(level: number): DerivedStats {
  const lv = Math.max(1, Math.min(MAX_LEVEL, Math.trunc(level)));
  return {
    maxHp: 120 + (lv - 1) * 12,
    atk: 18 + (lv - 1) * 3,
    defense: 5 + Math.trunc((lv - 1) / 2),
    spirit: 100 + (lv - 1) * 4,
    critRate: Math.min(0.05 + (lv - 1) * 0.002, 0.15),
    critDamage: 1.5,
    haste: Math.min((lv - 1) * 0.003, 0.12),
    breakPower: 10 + (lv - 1) * 2,
  };
}

export function comboMultiplier(combo: number): number {
  if (combo >= 50) return 1.08;
  if (combo >= 25) return 1.05;
  if (combo >= 10) return 1.03;
  return 1;
}

export function calculateDamage(
  level: number,
  powerMultiplier: number,
  combo: number,
  rngRoll: number,
): DamageResult {
  const stats = derivedStats(level);
  const critRate = stats.critRate + (combo >= 50 ? 0.05 : 0);
  const critical = rngRoll <= critRate;
  let amount = stats.atk * Math.max(powerMultiplier, 0.1) * comboMultiplier(combo);
  if (critical) amount *= stats.critDamage;
  return { damage: Math.max(1, Math.round(amount)), critical };
}

export function applyCombatAffinity(baseDamage: number, resolution: AffinityResolution): number {
  if (!Number.isFinite(baseDamage) || baseDamage <= 0) return 0;
  return Math.max(0, Math.round(baseDamage * resolution.totalDamageMultiplier));
}

export function applyBreakAffinity(baseBreak: number, resolution: AffinityResolution): number {
  if (!Number.isFinite(baseBreak) || baseBreak <= 0) return 0;
  return Math.max(0, Math.round(baseBreak * resolution.totalBreakMultiplier));
}

export function inHorizontalRange(originX: number, targetX: number, facing: -1 | 1, range: number): boolean {
  const dx = targetX - originX;
  return Math.abs(dx) <= range && dx * facing >= -18;
}
